/**
 * NoteSharesSection — partajare notițe cu membri tenant + revoke/rotate.
 * Paritate cu SharesSection din VaultDetail (credentials).
 *
 * Flow:
 * - List: GET /notes/{id}/shares
 * - Add: unwrap DEK cu cheia caller-ului, wrap cu public_key destinatar, POST
 * - Revoke + rotate: decrypt conținut → DEK nou → re-encrypt → wrap pt rămași →
 *   POST /rotate cu revoke=[user_id]. Zero-acces post-revoke.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { notesApi, type NoteDetail } from '@/api/notes';
import { errorMessage } from '@/api/client';
import { tenantsApi } from '@/api/tenants';
import {
  base64ToBytes,
  bytesToBase64,
  decryptSym,
  encryptSym,
  generateDEK,
  unwrapKey,
  wrapKey,
} from '@/crypto/sodium';
import { propagateAssetWraps } from '@/features/notes/assetWraps';
import { useAuthStore } from '@/stores/auth';
import { confirmDialog } from '@/stores/dialog';

interface Props {
  note: NoteDetail;
  tenantId: string;
  keyPair: { publicKey: Uint8Array; privateKey: Uint8Array };
}

export default function NoteSharesSection({ note, tenantId, keyPair }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [permission, setPermission] = useState<'read' | 'write'>('read');
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);

  const membersQuery = useQuery({
    queryKey: ['tenant-members', tenantId],
    queryFn: () => tenantsApi.listMembers(tenantId),
    enabled: expanded && !!tenantId,
  });

  const sharesQuery = useQuery({
    queryKey: ['note-shares', note.id],
    queryFn: () => notesApi.listShares(note.id),
    enabled: expanded,
  });

  const addMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      setError(null);
      const target = membersQuery.data?.find((m) => m.user_id === targetUserId);
      if (!target) throw new Error(t('notes_ui.shares.unknown_recipient'));
      // Unwrap DEK cu caller-ul, re-wrap cu public_key destinatar
      const dek = await unwrapKey(base64ToBytes(note.dek_wrapped), keyPair);
      try {
        const targetPub = base64ToBytes(target.public_key);
        const wrapped = await wrapKey(dek, targetPub);
        await notesApi.addShare(note.id, {
          user_id: targetUserId,
          dek_wrapped: bytesToBase64(wrapped),
          permission,
        });
      } finally {
        dek.fill(0);
      }
      // Atașamentele au DEK-uri proprii: fără pasul ăsta destinatarul vede
      // textul, dar primește 403 la imagini și fișiere.
      try {
        await propagateAssetWraps({ noteId: note.id, tenantId, keyPair });
      } catch {
        setError(t('notes_ui.shares.assets_propagation_failed'));
      }
    },
    onSuccess: () => {
      setSelectedUserId('');
      setPermission('read');
      queryClient.invalidateQueries({ queryKey: ['note-shares', note.id] });
    },
    onError: (err) => {
      setError(errorMessage(err, t('notes_ui.shares.share_error')));
    },
  });

  const rotateRevokeMutation = useMutation({
    mutationFn: async (userId: string) => {
      setError(null);
      if (!sharesQuery.data || !membersQuery.data) {
        throw new Error(t('notes_ui.shares.lists_missing'));
      }
      // 1. Decriptează HTML cu DEK-ul vechi via caller wrap
      const oldDek = await unwrapKey(base64ToBytes(note.dek_wrapped), keyPair);
      let plainBytes: Uint8Array;
      try {
        plainBytes = await decryptSym(
          {
            ciphertext: base64ToBytes(note.ciphertext),
            nonce: base64ToBytes(note.nonce),
          },
          oldDek,
        );
      } finally {
        oldDek.fill(0);
      }
      // 2. Filtrează remaining (fără userul revocat)
      const remaining = sharesQuery.data.filter((s) => s.user_id !== userId);
      if (remaining.length === 0) {
        throw new Error(t('notes_ui.shares.last_share'));
      }
      // 3. DEK nou + re-encrypt + wrap pt remaining
      const newDek = await generateDEK();
      try {
        const { ciphertext, nonce } = await encryptSym(plainBytes, newDek);
        const dek_wraps: Record<string, string> = {};
        for (const s of remaining) {
          const m = membersQuery.data.find((mm) => mm.user_id === s.user_id);
          if (!m) {
            throw new Error(t('notes_ui.shares.member_missing', { userId: s.user_id }));
          }
          const w = await wrapKey(newDek, base64ToBytes(m.public_key));
          dek_wraps[s.user_id] = bytesToBase64(w);
        }
        return notesApi.rotate(note.id, {
          ciphertext: bytesToBase64(ciphertext),
          nonce: bytesToBase64(nonce),
          dek_wraps,
          revoke: [userId],
          change_reason: 'revoke + rotate DEK',
        });
      } finally {
        newDek.fill(0);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note-shares', note.id] });
      queryClient.invalidateQueries({ queryKey: ['note', note.id] });
    },
    onError: (err) => {
      setError(errorMessage(err, t('notes_ui.shares.rotate_error')));
    },
  });

  const availableTargets =
    membersQuery.data?.filter(
      (m) =>
        m.user_id !== currentUserId &&
        !sharesQuery.data?.some((s) => s.user_id === m.user_id),
    ) ?? [];

  return (
    <section className="pt-4 mt-4 border-t border-[var(--border)]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 section-label no-dot hover:text-[var(--text)]"
      >
        <span>{expanded ? '▼' : '▶'}</span>
        {t('notes_ui.shares.title')}
        {sharesQuery.data && (
          <span className="text-[10px] normal-case text-[var(--text-dim)]">
            ({sharesQuery.data.length})
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {sharesQuery.isLoading && (
            <p className="text-xs text-[var(--text-muted)]">{t('common.loading')}</p>
          )}
          {sharesQuery.data && (
            <div className="space-y-1">
              {sharesQuery.data.map((s) => (
                <div
                  key={s.user_id}
                  className="flex items-center justify-between text-sm border border-[var(--border)] rounded px-3 py-2"
                >
                  <div>
                    <span className="font-medium">{s.email}</span>
                    <span className="ml-2 badge badge-accent">{s.permission}</span>
                  </div>
                  {s.permission !== 'owner' && (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50 font-medium"
                        onClick={async () => {
                          if (
                            await confirmDialog(t('notes_ui.shares.revoke_rotate_confirm', { email: s.email }))
                          ) {
                            rotateRevokeMutation.mutate(s.user_id);
                          }
                        }}
                        disabled={rotateRevokeMutation.isPending}
                        title={t('notes_ui.shares.revoke_rotate_title')}
                      >
                        {rotateRevokeMutation.isPending ? t('notes_ui.shares.rotating') : t('notes_ui.shares.revoke_rotate')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {availableTargets.length > 0 ? (
            <div className="flex gap-2">
              <select
                className="input text-sm"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={addMutation.isPending}
              >
                <option value="">{t('notes_ui.shares.choose_member')}</option>
                {availableTargets.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.email} ({m.role})
                  </option>
                ))}
              </select>
              <select
                className="input text-sm w-auto"
                aria-label={t('sharing.permission')}
                value={permission}
                onChange={(e) => setPermission(e.target.value as 'read' | 'write')}
                disabled={addMutation.isPending}
              >
                <option value="read">{t('sharing.read')}</option>
                <option value="write">{t('sharing.write')}</option>
              </select>
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => selectedUserId && addMutation.mutate(selectedUserId)}
                disabled={!selectedUserId || addMutation.isPending}
              >
                {addMutation.isPending ? t('notes_ui.shares.sharing') : t('notes_ui.shares.share')}
              </button>
            </div>
          ) : membersQuery.data && membersQuery.data.length <= 1 ? (
            <p className="text-xs text-[var(--text-muted)]">
              {t('notes_ui.shares.no_other_members')}
            </p>
          ) : (
            <p className="text-xs text-[var(--text-muted)]">
              {t('notes_ui.shares.shared_with_all')}
            </p>
          )}

          {error && (
            <div
              className="text-xs rounded px-3 py-2"
              style={{
                color: 'var(--danger)',
                background: 'var(--danger-bg)',
                border: '1px solid var(--danger)',
              }}
            >
              {error}
            </div>
          )}

          <p className="text-[10px] text-[var(--text-dim)] italic">
            {t('notes_ui.shares.hint')}
          </p>
        </div>
      )}
    </section>
  );
}
